import { compilePredicate } from './query.js';
import { compileFormula } from './formula.js';
export const OP_LABEL = {
    '=': '같음', '!=': '같지 않음', '>': '초과', '>=': '이상', '<': '미만', '<=': '이하', contains: '포함', notContains: '포함 안 함',
    startsWith: '시작', endsWith: '끝', like: '패턴(*,?)', notLike: '패턴 아님', in: '목록 중', notIn: '목록 아님', between: '범위', notBetween: '범위 밖',
    empty: '빈 값', notEmpty: '값 있음',
};
export const OP_SYMBOL = { '=': '=', '!=': '≠', '>': '>', '>=': '≥', '<': '<', '<=': '≤', in: '∈', notIn: '∉' };
export const STRING_OPS = ['contains', 'notContains', 'startsWith', 'endsWith', '=', '!=', 'like', 'notLike', 'in', 'notIn', 'empty', 'notEmpty'];
export const RANGE_OPS = ['=', '!=', '>=', '>', '<=', '<', 'between', 'notBetween', 'in', 'notIn', 'empty', 'notEmpty'];
/** 필터 트리 → 행 판정 함수 */
export function compileFilter(ds, n) {
    if (n.kind === 'expr') {
        const f = compileFormula(n.expr);
        return r => { const v = f({ ds, row: r }); return !(v == null || v === false || v === 0 || v === ''); };
    }
    if (n.kind === 'cond') {
        const value = (n.op === 'in' || n.op === 'notIn') && typeof n.value === 'string' ? n.value.split(',').map((s) => s.trim()).filter(Boolean) : n.value;
        const t = compilePredicate(ds, { col: n.col, op: n.op, value, value2: n.value2 });
        if (!t)
            return () => true;
        return n.not ? r => !t(r) : t;
    }
    const parts = n.items.map(i => compileFilter(ds, i));
    let f;
    if (!parts.length)
        f = () => true;
    else if (n.logic === 'AND')
        f = r => { for (const p of parts)
            if (!p(r))
                return false; return true; };
    else
        f = r => { for (const p of parts)
            if (p(r))
                return true; return false; };
    return n.not ? r => !f(r) : f;
}
const T0 = (k, v) => (v ? k.replace(/\{(\w+)\}/g, (m, x) => (x in v ? String(v[x]) : m)) : k);
/** 사람이 읽는 필터 문장. t 를 주면 연산자·문구를 번역 (예: grid.t) */
export function describeFilter(n, label = c => c, fmt = (_c, v) => String(v), t = T0) {
    if (n.kind === 'expr')
        return n.expr;
    if (n.kind === 'cond') {
        const L = label(n.col);
        const v = Array.isArray(n.value) ? `(${n.value.map(x => fmt(n.col, x)).join(', ')})` : fmt(n.col, n.value);
        let s;
        switch (n.op) {
            case 'empty':
                s = `${L} ${t('빈 값')}`;
                break;
            case 'notEmpty':
                s = `${L} ${t('값 있음')}`;
                break;
            case 'between':
                s = `${L} ${fmt(n.col, n.value)} ~ ${fmt(n.col, n.value2)}`;
                break;
            case 'notBetween':
                s = `${L} ${t('{a} ~ {b} 밖', { a: fmt(n.col, n.value), b: fmt(n.col, n.value2) })}`;
                break;
            default: s = OP_SYMBOL[n.op] ? `${L} ${OP_SYMBOL[n.op]} ${v}` : `${L} ${t(OP_LABEL[n.op])} '${v}'`;
        }
        return n.not ? `NOT (${s})` : s;
    }
    const inner = n.items.map(i => (i.kind === 'group' && i.items.length > 1 ? `(${describeFilter(i, label, fmt, t)})` : describeFilter(i, label, fmt, t))).join(` ${n.logic} `);
    return n.not ? `NOT (${inner})` : inner;
}
export function emptyGroup() { return { kind: 'group', logic: 'AND', items: [] }; }
// ───────────── 날짜 프리셋 ─────────────
export const DATE_PRESETS = [
    ['today', '오늘'], ['yesterday', '어제'], ['last7', '최근 7일'], ['last30', '최근 30일'], ['thisWeek', '이번 주'], ['lastWeek', '지난주'],
    ['thisMonth', '이번 달'], ['lastMonth', '지난달'], ['thisYear', '올해'], ['past', '오늘 이전'], ['future', '오늘 이후'],
];
/** 날짜 프리셋 목록 (번역된 이름) — [키, 이름][] */
export function datePresetLabels(t = T0) { return DATE_PRESETS.map(([k, l]) => [k, t(l)]); }
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** 프리셋 → 조건 (오늘 기준으로 매번 계산) */
export function datePresetCond(p, now = new Date()) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const add = (n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
    const dow = (d.getDay() + 6) % 7; // 월요일 = 0
    switch (p) {
        case 'today': return { op: 'between', value: ymd(d), value2: ymd(d) };
        case 'yesterday': return { op: 'between', value: ymd(add(-1)), value2: ymd(add(-1)) };
        case 'last7': return { op: 'between', value: ymd(add(-6)), value2: ymd(d) };
        case 'last30': return { op: 'between', value: ymd(add(-29)), value2: ymd(d) };
        case 'thisWeek': return { op: 'between', value: ymd(add(-dow)), value2: ymd(add(6 - dow)) };
        case 'lastWeek': return { op: 'between', value: ymd(add(-dow - 7)), value2: ymd(add(-dow - 1)) };
        case 'thisMonth': return { op: 'between', value: ymd(new Date(d.getFullYear(), d.getMonth(), 1)), value2: ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
        case 'lastMonth': return { op: 'between', value: ymd(new Date(d.getFullYear(), d.getMonth() - 1, 1)), value2: ymd(new Date(d.getFullYear(), d.getMonth(), 0)) };
        case 'thisYear': return { op: 'between', value: `${d.getFullYear()}-01-01`, value2: `${d.getFullYear()}-12-31` };
        case 'past': return { op: '<', value: ymd(d) };
        case 'future': return { op: '>', value: ymd(d) };
    }
}
export function parseFind(q) {
    const out = [];
    const re = /([+-]?)(?:([^\s:"]+):)?(?:"([^"]*)"|(\S+))/g;
    let m;
    while ((m = re.exec(q))) {
        const text = (m[3] ?? m[4] ?? '').trim();
        if (!text)
            continue;
        out.push({ text: text.toLowerCase(), col: m[2], neg: m[1] === '-' });
    }
    return out;
}
export function termMatcher(t) {
    if (/[*?]/.test(t.text)) {
        const re = new RegExp(t.text.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.'), 'i');
        return s => re.test(s);
    }
    return s => s.toLowerCase().includes(t.text);
}
